import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import { SOARResources, residenceClientAssginment } from "../services/soar";
import FloatingInput from "../common/FloatingInput";
import CensusCell from "./CensusCell";

function DailyServices(props) {
  const [data, setData] = useState([]);
  const [searchData, setSearchData] = useState({
    startDate: "2024-11-01",
    endDate: "",
    company: "",
    hideConfirmed: true,
    filterBy: "state", // Default search by state
    filterOptions: [],
  });
  const navigate = useNavigate();
  const applicationId =
    process.env.REACT_APP_ENVIRONMENT === "Production"
      ? "5dae139e-d052-4bd8-bf4a-499a31ac3c15"
      : "58990f83-8566-4ba8-b4b5-584a64e4cba1";

  const getDailyServices = async () => {
    const data = await residenceClientAssginment({
      command: "query",
      application: applicationId,
      keyword: "datescompanyinterms",
      searchTerms: {
        startDate: searchData.startDate,
        endDate: searchData.endDate,
        company: searchData.company,
      },
    });
    if (data.code !== "success") {
      toast.error("Error fetching services:", data.message);
      return;
    }

    const uniqueClients = [...new Set(data.data.map((item) => item.client))];
    getClientInfo(uniqueClients, data.data);
  };

  const getClientInfo = async (clientIds, allServices) => {
    const data = await SOARResources({
      command: "query",
      application: applicationId,
      keyword: "modalityandcaregiver",
      resource: "censusinfo",
      ids: clientIds,
    });
    if (data.code !== "success") {
      toast.error("Error fetching client info:", data.message);
      return null;
    }
    // console.log("Client Info Data:", data.data);
    const clientData = data?.data?.map((client) => ({
      key: client.id,
      id: client.id,
      mpi: client.mpi,
      dob: client.dob,
      payors:
        allServices.find((service) => service.client === client.id)?.payors ||
        [],
      modalities: client.modalities || [],
      caregiver: client.caregiver || [],
    }));

    const allData = allServices.map((service) => ({
      key: service.id,
      ...service,
      modalities:
        clientData.find((info) => info.id === service.client)?.modalities || [],
      caregiver:
        clientData.find((info) => info.id === service.client)?.caregiver || [],
      dob: clientData.find((info) => info.id === service.client)?.dob,
      mpi: clientData.find((info) => info.id === service.client)?.mpi,
    }));
    // console.log("All Data with Client Info:", allData);
    setData(allData);
  };

  const filterData = () => {
    switch (searchData.filterBy) {
      case "state":
        return data;
      case "client":
        return data.filter((item) =>
          item.client
            ?.toLowerCase()
            .includes(searchData.filterValue?.toLowerCase())
        );
      case "program":
        return data.filter((item) =>
          item.program
            ?.toLowerCase()
            .includes(searchData.filterValue?.toLowerCase())
        );
      case "worker":
        return data.filter(
          (item) =>
            item.caregiver &&
            item.caregiver.some(
              (cg) =>
                cg.ccx_memberid &&
                cg.ccx_memberid.toLowerCase() ===
                  searchData.filterValue?.toLowerCase()
            )
        );

      default:
        break;
    }
  };

  const uniquePrograms = Array.from(
    data
      .reduce((map, item) => {
        if (item.program && item.programName) {
          map.set(item.program, {
            _id: item.program,
            name: item.programName,
          });
        }
        return map;
      }, new Map())
      .values()
  ).sort((a, b) => a.name.localeCompare(b.name));

  const uniqueClients = Array.from(
    data
      .reduce((map, item) => {
        if (item.client && item.clientName) {
          map.set(item.client, {
            _id: item.client,
            name: item.clientName,
          });
        }
        return map;
      }, new Map())
      .values()
  ).sort((a, b) => a.name.localeCompare(b.name));

  const uniqueWorkers = Array.from(
    data
      .reduce((map, item) => {
        if (item.caregiver && Array.isArray(item.caregiver)) {
          item.caregiver.forEach((cg) => {
            if (cg.ccx_memberid && cg.ccx_membername) {
              map.set(cg.ccx_memberid, {
                _id: cg.ccx_memberid,
                name: cg.ccx_membername,
              });
            }
          });
        }
        return map;
      }, new Map())
      .values()
  ).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="">
      <h4>Daily Services</h4>
      <div className="rounded border border-dark bg-light p-2 mb-4">
        <div className="h5">Census Search</div>
        <div className="row mb-4">
          <div className="col">
            <FloatingInput
              type="select"
              name="company"
              label="State"
              options={[
                {
                  _id: "6C0C4523-44BF-E211-90BC-005056822045",
                  name: "SAFY of Alabama",
                },
                {
                  _id: "8856675B-ABE2-E411-B77B-005056822045",
                  name: "SAFY of Colorado",
                },
                {
                  _id: "8F00A03B-14A0-E511-8ACA-005056822045",
                  name: "SAFY of Indiana",
                },
                {
                  _id: "97D87A49-14A0-E511-8ACA-005056822045",
                  name: "SAFY of Kentucky",
                },
                {
                  _id: "137D0EF9-13A0-E511-8ACA-005056822045",
                  name: "SAFY of Nevada",
                },
                {
                  _id: "1C16C69A-4544-E611-8588-005056822045",
                  name: "SAFY of Ohio",
                },
                {
                  _id: "A300FDAD-9B16-E511-8ACA-005056822045",
                  name: "SAFY of Oklahoma",
                },
                {
                  _id: "B699545E-2541-E511-8ACA-005056822045",
                  name: "SAFY of South Carolina",
                },
                {
                  _id: "70EF70D3-8C8F-EE11-A829-000D3A18243E",
                  name: "SAFY OF TESTING",
                },
              ]}
              value={searchData.company}
              onChange={(e) =>
                setSearchData({ ...searchData, company: e.target.value })
              }
            />
          </div>
          <div className="col">
            <FloatingInput
              type="date"
              name="startDate"
              label="Start Date"
              value={searchData.startDate}
              onChange={(e) =>
                setSearchData({ ...searchData, startDate: e.target.value })
              }
            />
          </div>
          <div className="col">
            <FloatingInput
              type="date"
              name="endDate"
              label="End Date"
              value={searchData.endDate}
              onChange={(e) =>
                setSearchData({ ...searchData, endDate: e.target.value })
              }
            />
          </div>
          <div className="col ">
            <button
              className="btn btn-primary d-inline-block mt-2"
              onClick={getDailyServices}
              disabled={
                !searchData.startDate ||
                !searchData.endDate ||
                !searchData.company
              }
            >
              Search
            </button>
          </div>
        </div>
        {data.length > 0 ? (
          <div className="row">
            <div className="col-1">Filter By</div>
            <div className="col-5">
              <div
                className={`btn m-1 ${
                  searchData.filterBy === "state" ? "btn-info" : "btn-primary"
                }`}
                onClick={() =>
                  setSearchData({
                    ...searchData,
                    filterBy: "state",
                    filterOptions: [],
                  })
                }
              >
                State
              </div>
              <div
                className={`btn m-1 ${
                  searchData.filterBy === "program" ? "btn-info" : "btn-primary"
                }`}
                onClick={() =>
                  setSearchData({
                    ...searchData,
                    filterBy: "program",
                    filterOptions: uniquePrograms,
                  })
                }
              >
                Program
              </div>
              <div
                className={`btn m-1 ${
                  searchData.filterBy === "client" ? "btn-info" : "btn-primary"
                }`}
                onClick={() =>
                  setSearchData({
                    ...searchData,
                    filterBy: "client",
                    filterOptions: uniqueClients,
                  })
                }
              >
                Client
              </div>
              <div
                className={`btn m-1 ${
                  searchData.filterBy === "worker" ? "btn-info" : "btn-primary"
                }`}
                onClick={() =>
                  setSearchData({
                    ...searchData,
                    filterBy: "worker",
                    filterOptions: uniqueWorkers,
                  })
                }
              >
                Worker
              </div>
            </div>
            <div className="col-3">
              {searchData.filterBy !== "state" && (
                <FloatingInput
                  type="select"
                  name="filterValue"
                  label={`Filter by ${searchData.filterBy}`}
                  options={searchData.filterOptions}
                  value={searchData.filterValue}
                  onChange={(e) =>
                    setSearchData({
                      ...searchData,
                      filterValue: e.target.value,
                    })
                  }
                />
              )}
            </div>
            <div className="col-3">
              <div className="d-inline-block ms-4 form-check form-switch form-check-reverse">
                {/* <FloatingInput
              type="switch"
              name="hideConfirmed"
              label="Hide Confirmed"
              checked={searchData.hideConfirmed}
              onChange={(e) => {
                setSearchData({
                  ...searchData,
                  hideConfirmed: e.target.checked,
                });
              }}
            /> */}
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="expired"
                  checked={searchData.hideConfirmed}
                  onChange={(e) => {
                    setSearchData({
                      ...searchData,
                      hideConfirmed: e.target.checked,
                    });
                  }}
                />
                <label className="form-check-label" htmlFor="expired">
                  Hide Confirmed
                </label>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      {filterData().map((bedAssignment) => (
        <div key={bedAssignment.id} className="card card-safy mb-3">
          <div className="card-header bg-safy1-1 text-white fw-light">
            <div className="row mb-1">
              <div className="col">
                <div className="row">
                  <div
                    className="col btn btn-info border border-dark text-start"
                    onClick={() => navigate(`/client/${bedAssignment.client}`)}
                  >
                    <i className="fas fa-user me-2" />
                    {bedAssignment.clientName}
                  </div>
                  <div className="col">{`MPI: ${bedAssignment?.mpi}`}</div>
                  <div className="col">
                    {`DOB: ${DateTime.fromISO(
                      bedAssignment?.dob
                    ).toLocaleString(DateTime.DATE_SHORT)}`}
                  </div>
                </div>
              </div>
              <div className="col">
                <div className="row">
                  <div
                    className="col btn btn-success border border-white text-start"
                    onClick={() =>
                      navigate(
                        `/residence/fosterhomes/${bedAssignment.residence}`
                      )
                    }
                  >
                    <i className="fas fa-home me-2" />
                    {bedAssignment.residenceName}
                  </div>
                  <div className="col">
                    <div className="fw-bold">
                      {DateTime.fromISO(bedAssignment.admission).toLocaleString(
                        DateTime.DATE_SHORT
                      )}
                      {bedAssignment.discharge
                        ? ` - ${DateTime.fromISO(
                            bedAssignment.discharge
                          ).toLocaleString(DateTime.DATE_SHORT)}`
                        : ""}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card-body">
            <div className="d-flex flex-wrap">
              {bedAssignment.services?.map((service) => (
                <CensusCell
                  key={service.id}
                  bedAssignment={bedAssignment}
                  service={{
                    ...service,
                    serviceStatuses:
                      bedAssignment.serviceConfigurationValue.serviceStatuses,
                  }}
                  showConfirmed={!searchData.hideConfirmed}
                  setData={setData}
                />
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default DailyServices;
